import React, { useState, useRef, useEffect } from "react";

interface AutocompleteInputProps {
  id: string;
  label: string;
  placeholder?: string;
  manifestNames: Record<string, string>;
  value: string;
  onChange: (name: string, id: string) => void;
  error?: string;
  required?: boolean;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  id,
  label,
  placeholder = "Type to search...",
  manifestNames,
  value,
  onChange,
  error,
  required = false,
}) => {
  const [displayValue, setDisplayValue] = useState<string>("");
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (inputValue: string) => {
    setDisplayValue(inputValue);
    
    if (inputValue.trim().length > 0) {
      const suggestions = Object.keys(manifestNames).filter((name) =>
        name.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      onChange("", "");
    }
  };

  const handleSuggestionSelect = (selectedName: string) => {
    setDisplayValue(selectedName);
    const manifestId = manifestNames[selectedName];
    onChange(selectedName, manifestId);
    setFilteredSuggestions([]);
    setShowSuggestions(false);
  };

  const handleClear = () => {
    setDisplayValue("");
    onChange("", "");
    setFilteredSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        <input
          type="text"
          id={id}
          value={displayValue}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className={`w-full border rounded-lg p-2.5 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        />
        
        {/* Clear button */}
        {displayValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg mt-1 shadow-lg max-h-48 overflow-y-auto">
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionSelect(suggestion)}
              className="p-3 hover:bg-blue-50 cursor-pointer text-sm transition-colors border-b last:border-b-0"
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default AutocompleteInput;